import produce from "immer";
import { noop, uniq } from "lodash";
import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { None, Option, Some } from "space-monad";
import { useFetchable } from "../../../../store/common/fetchable";
import { Users } from "../../../../store/users/reducer";
import { VerificationDTO } from "../../../../store/verification/models/dto";
import { VerificationDTO as KYCVerificationDTO } from "../../../../store/verification/models/dto/kyc";
import {
  DeclineCode,
  ServiceType,
} from "../../../../store/verification/models/internal";
import { VerificationService } from "../../../../store/verification/service";
import { mapObject } from "../../../../utils/utils";

export interface ISingleKyc {
  newDeclineCodes: Partial<
    Record<typeof ServiceType.T, Array<typeof DeclineCode.T>>
  >;
  originalDeclineCodes: Partial<
    Record<typeof ServiceType.T, Array<typeof DeclineCode.T>>
  >;
  allDeclineCodes: Option<VerificationDTO.Responses.GetDeclineCodes.T>;
  addDeclineCode: (
    declineCode: typeof DeclineCode.T,
    service: typeof ServiceType.T
  ) => void;
  removeDeclineCode: (
    declineCode: typeof DeclineCode.T,
    service: typeof ServiceType.T
  ) => void;
  setItem: Dispatch<
    SetStateAction<KYCVerificationDTO.Responses.GetSingleKyc.T | undefined>
  >;
  changesAmount: number;
  changedServices: Array<typeof ServiceType.T>;
  item: KYCVerificationDTO.Responses.GetSingleKyc.T | undefined;
}

export const SingleKycContext = createContext<ISingleKyc>(
  (undefined as any) as ISingleKyc
);

export const SingleKycProvider: FC<{}> = (props) => {
  const [allDeclineCodes, setAllDeclineCodes] = useState<
    Option<VerificationDTO.Responses.GetDeclineCodes.T>
  >(None);

  const [
    item,
    setItem,
  ] = useState<KYCVerificationDTO.Responses.GetSingleKyc.T>();

  const originalDeclineCodes: ISingleKyc["originalDeclineCodes"] = useMemo(
    () =>
      item
        ? item.serviceDetails.reduce(
            (acc, { service, details }) => ({
              ...acc,
              [service]:
                details.status === "DECLINED"
                  ? details.declineCodes.map((x) => x.code)
                  : [],
            }),
            {}
          )
        : {},
    [item]
  );

  const [newDeclineCodes, setNewDeclineCodes] = useState<
    ISingleKyc["newDeclineCodes"]
  >(originalDeclineCodes);

  useEffect(() => {
    setNewDeclineCodes(originalDeclineCodes);
  }, [originalDeclineCodes]);

  const user = useFetchable(Users.current);

  useEffect(() => {
    if (user.status === "ready")
      VerificationService.getDeclineCodes()
        .then((x) => setAllDeclineCodes(Some(x)))
        .catch(noop);
  }, [user.status]);

  const addDeclineCode = useCallback(
    (declineCode: typeof DeclineCode.T, service: typeof ServiceType.T) =>
      setNewDeclineCodes((y) =>
        produce(y, (state) => {
          if (state[service] !== undefined) {
            state[service]?.push(declineCode);
          } else {
            state[service] = [declineCode];
          }
        })
      ),
    []
  );

  const removeDeclineCode = useCallback(
    (declineCode: typeof DeclineCode.T, service: typeof ServiceType.T) =>
      setNewDeclineCodes((y) =>
        produce(y, (state) => {
          if (state[service] !== undefined) {
            state[service] = state[service]?.filter((x) => x !== declineCode);
          }
        })
      ),
    []
  );

  const changesAmount = useMemo(
    () =>
      mapObject(
        originalDeclineCodes,
        (val, key) =>
          val.filter((x) => !(newDeclineCodes[key] || []).includes(x)).length
      ).reduce((acc, curr) => acc + curr, 0) +
      mapObject(
        newDeclineCodes,
        (val, key) =>
          val.filter((x) => !(originalDeclineCodes[key] || []).includes(x))
            .length
      ).reduce((acc, curr) => acc + curr, 0),
    [newDeclineCodes, originalDeclineCodes]
  );

  const changedServices = useMemo(
    () =>
      uniq(
        mapObject(originalDeclineCodes, (val, key) =>
          val.filter((x) => !(newDeclineCodes[key] || []).includes(x)).length >
          0
            ? [key]
            : []
        )
          .reduce((acc, curr) => [...acc, ...curr], [])
          .concat(
            mapObject(newDeclineCodes, (val, key) =>
              val.filter((x) => !(originalDeclineCodes[key] || []).includes(x))
                .length > 0
                ? [key]
                : []
            ).reduce((acc, curr) => [...acc, ...curr], [])
          )
      ),
    [originalDeclineCodes, newDeclineCodes]
  );

  return (
    <SingleKycContext.Provider
      value={{
        item,
        setItem,
        allDeclineCodes,
        originalDeclineCodes,
        newDeclineCodes,
        removeDeclineCode,
        addDeclineCode,
        changesAmount,
        changedServices,
      }}
      {...props}
    ></SingleKycContext.Provider>
  );
};
